import React, { useState } from 'react';
import TmuaPearsonShell, {
  DoorIcon,
  NextIcon,
  TmuaPearsonFooterButton,
  useTmuaPearsonAccessKeys,
  getPearsonPlayerExamTitle,
} from './TmuaPearsonShell';
import TmuaPearsonDialog from './TmuaPearsonDialog';
import { TMUA_PEARSON_TOKENS } from '../constants/tmuaPearsonTokens';
import { ESAT_MODULE_LABELS } from '../utils/classifyEsatModule';
import {
  ESAT_OPTIONAL_ORDER,
  getEsatPickerResponseRequiredMessage,
  isEsatPickerSelectionValid,
} from '../utils/esatModulePicker';

export default function EsatModulePickerScreen({
  examName = 'ESAT',
  heritageExam,
  optionals = {},
  selectedOptionalKeys = [],
  onChangeSelected,
  onNext,
  onEndExam,
  colorScheme = 'pearsonDefault',
  setColorScheme,
  compulsoryLabel = ESAT_MODULE_LABELS.mathematics_1,
  bodyCopy = 'Mathematics 1 is required; choose two further modules.',
  factLine = null,
  requiredCount = 2,
  optionalKeys = ESAT_OPTIONAL_ORDER,
  optionalLabels = ESAT_MODULE_LABELS,
}) {
  const [showEndExam, setShowEndExam] = useState(false);
  const [showRequired, setShowRequired] = useState(false);
  const T = TMUA_PEARSON_TOKENS;
  const overlayOpen = showEndExam || showRequired;

  const handleNext = () => {
    if (!isEsatPickerSelectionValid(selectedOptionalKeys, requiredCount)) {
      setShowRequired(true);
      return;
    }
    onNext(selectedOptionalKeys);
  };

  const toggleOptional = (key, enabled) => {
    if (!enabled) return;
    const selected = selectedOptionalKeys.includes(key);
    const next = selected
      ? selectedOptionalKeys.filter((item) => item !== key)
      : [...selectedOptionalKeys, key];
    onChangeSelected(next);
  };

  useTmuaPearsonAccessKeys(
    {
      showEndExamModalVisible: showEndExam,
      showResponseRequiredVisible: showRequired,
      nextAvailable: !overlayOpen,
      endExamAvailable: !showRequired,
    },
    {
      onNext: () => { if (!overlayOpen) handleNext(); },
      onEndExam: () => setShowEndExam(true),
      onConfirmYes: () => { if (showEndExam) onEndExam(); },
      onConfirmNo: () => setShowEndExam(false),
      onDismissOk: () => { if (showRequired) setShowRequired(false); },
    }
  );

  return (
    <TmuaPearsonShell
      examName={examName}
      title={getPearsonPlayerExamTitle(examName, undefined, { heritageExam })}
      heritageExam={heritageExam}
      colorScheme={colorScheme}
      setColorScheme={setColorScheme}
      onBackToSelection={onEndExam}
      footerLeft={(
        <TmuaPearsonFooterButton onClick={() => setShowEndExam(true)} icon={<DoorIcon color={T.titleBarFg} />} underline="e" keepLabelOnMobile>
          End Exam
        </TmuaPearsonFooterButton>
      )}
      footerRight={(
        <TmuaPearsonFooterButton variant="chrome" onClick={handleNext} icon={<NextIcon color="#FFFFFF" />} iconPosition="end" underline="n">
          Next
        </TmuaPearsonFooterButton>
      )}
    >
      <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: '18px 22px', color: '#000', fontFamily: T.fontUi, fontSize: 16, lineHeight: 1.35 }}>
        <p style={{ margin: '0 0 10px', color: 'var(--iv-body)' }}>
          Choose the modules for this sitting.
        </p>
        <p style={{ margin: '0 0 16px', color: 'var(--iv-body)' }}>
          {bodyCopy}
        </p>
        {factLine ? (
          <p style={{ margin: '0 0 16px', color: 'var(--iv-body)' }}>
            {factLine}
          </p>
        ) : null}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', color: 'var(--iv-body)' }}>
          <input type="checkbox" checked disabled />
          <span style={{ fontWeight: 700 }}>{compulsoryLabel}</span>
        </label>
        {optionalKeys.map((key) => {
          const enabled = Boolean(optionals[key]);
          const checked = selectedOptionalKeys.includes(key);
          const label = optionalLabels[key] || ESAT_MODULE_LABELS[key] || key;
          return (
            <label
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '4px 0',
                color: 'var(--iv-body)',
                cursor: enabled ? 'pointer' : 'default',
                opacity: enabled ? 1 : 0.55,
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={!enabled}
                onChange={() => toggleOptional(key, enabled)}
              />
              <span style={{ fontWeight: 700 }}>{label}</span>
            </label>
          );
        })}
      </div>
      {showRequired ? (
        <TmuaPearsonDialog title="Response Required" onOk={() => setShowRequired(false)}>
          {getEsatPickerResponseRequiredMessage(selectedOptionalKeys, requiredCount)}
        </TmuaPearsonDialog>
      ) : null}
      {showEndExam ? (
        <TmuaPearsonDialog
          title="End Exam"
          info
          onYes={onEndExam}
          onNo={() => setShowEndExam(false)}
        >
          You have chosen to end this exam. Are you sure you want to end this exam?
        </TmuaPearsonDialog>
      ) : null}
    </TmuaPearsonShell>
  );
}
